import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { RoleTypes } from "../../auth/types/roleTypes";
import { Role } from "../../auth/types/authType";
import { isPrivilegedTransaction } from "../../../utils/authUtils";
import {
  ComplainStatus,
  Complaint,
  UpdateComplaintStatusInput,
} from "../types/complaintTypes";

type ComplaintStatusActionsProps = {
  complaint: Complaint;
  roles: Role[];
  onStatusChange: (input: UpdateComplaintStatusInput) => void;
  loading?: boolean;
};

export const ComplaintStatusActions = ({
  complaint,
  roles,
  onStatusChange,
  loading,
}: ComplaintStatusActionsProps) => {
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<ComplainStatus | null>(
    null,
  );
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [syncEmployeeStatus, setSyncEmployeeStatus] = useState(false);

  const canEdit = isPrivilegedTransaction(roles, RoleTypes.EditComplains);
  const canClose = isPrivilegedTransaction(roles, RoleTypes.ClosComplains);
  const canConfirm = isPrivilegedTransaction(roles, RoleTypes.ConfirmComplains);
  const canReopen = isPrivilegedTransaction(roles, RoleTypes.ReOpenComplains);
  const canSyncDisposition = isPrivilegedTransaction(
    roles,
    RoleTypes.ChangeEmployeeDisposition,
  );

  const openDialog = (status: ComplainStatus) => {
    setPendingStatus(status);
    setResolutionNotes(complaint.resolutionNotes ?? "");
    setSyncEmployeeStatus(false);
    setDialogOpen(true);
  };

  const handleConfirm = () => {
    if (!pendingStatus || !complaint._id) return;
    onStatusChange({
      id: complaint._id,
      status: pendingStatus,
      resolutionNotes: resolutionNotes.trim() || undefined,
      syncEmployeeStatus:
        pendingStatus === ComplainStatus.confirmed && syncEmployeeStatus
          ? true
          : undefined,
    });
    setDialogOpen(false);
    setPendingStatus(null);
  };

  const { status } = complaint;

  const actions = useMemo(() => {
    const items: Array<{
      key: string;
      label: string;
      color: "primary" | "warning" | "success" | "error";
      variant: "contained" | "outlined";
      nextStatus: ComplainStatus;
    }> = [];

    if (
      (status === ComplainStatus.opened || status === ComplainStatus.reopened) &&
      canEdit
    ) {
      items.push({
        key: "start",
        label: t("startProcessing"),
        color: "primary",
        variant: "contained",
        nextStatus: ComplainStatus.on_process,
      });
    }

    if (
      (status === ComplainStatus.opened ||
        status === ComplainStatus.reopened ||
        status === ComplainStatus.on_process) &&
      canClose
    ) {
      items.push({
        key: "close",
        label: t("closeComplaint"),
        color: "warning",
        variant: "contained",
        nextStatus: ComplainStatus.closed,
      });
    }

    if (status === ComplainStatus.closed && canConfirm) {
      items.push({
        key: "confirm",
        label: t("confirmComplaint"),
        color: "success",
        variant: "contained",
        nextStatus: ComplainStatus.confirmed,
      });
    }

    if (status === ComplainStatus.closed && canReopen) {
      items.push({
        key: "reopen",
        label: t("reopenComplaint"),
        color: "error",
        variant: "outlined",
        nextStatus: ComplainStatus.reopened,
      });
    }

    return items;
  }, [status, canEdit, canClose, canConfirm, canReopen, t]);

  const needsResolutionNotes =
    pendingStatus === ComplainStatus.closed ||
    pendingStatus === ComplainStatus.confirmed ||
    pendingStatus === ComplainStatus.reopened;

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle2" gutterBottom>
        {t("statusActions")}
      </Typography>
      {actions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {status === ComplainStatus.confirmed
            ? t("complaintStatusFinal")
            : t("noStatusActionsAvailable")}
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {actions.map((action) => (
            <Button
              key={action.key}
              variant={action.variant}
              color={action.color}
              size="small"
              disabled={loading}
              onClick={() => openDialog(action.nextStatus)}
            >
              {action.label}
            </Button>
          ))}
        </Stack>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("updateComplaintStatus")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {pendingStatus &&
              t("confirmStatusChange", { status: te(pendingStatus) })}
          </DialogContentText>
          {needsResolutionNotes && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              label={t("resolutionNotes")}
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}
          {pendingStatus === ComplainStatus.confirmed && (
            <Tooltip
              title={
                canSyncDisposition
                  ? t("syncEmployeeDispositionHint")
                  : t("syncEmployeeDispositionDenied")
              }
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={syncEmployeeStatus}
                    onChange={(e) => setSyncEmployeeStatus(e.target.checked)}
                    disabled={!canSyncDisposition}
                  />
                }
                label={t("syncEmployeeDisposition")}
              />
            </Tooltip>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t("cancel")}</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={loading}>
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
